import { type DBSchema, type IDBPDatabase, openDB } from 'idb';

/**
 * The only IndexedDB database this app owns.
 *
 * It holds exactly one thing: writes the shop made while the connection was down. This is
 * NOT an offline-first replica of the shop's data — there is no local copy of orders, no
 * client-side schema migration and no conflict resolution. Cached READS come from the
 * TanStack Query cache; this store exists so that a payment recorded in a basement changing
 * room is not lost when the phone loses signal, and so it survives the owner killing the tab.
 */

export const OFFLINE_DB_NAME = 'tailoring-crm';
export const OFFLINE_DB_VERSION = 1;
export const WRITE_QUEUE_STORE = 'write-queue';

/** Methods that change something and are therefore worth queueing. */
export type QueueableMethod = 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export type QueuedWriteStatus =
  /** Waiting for the connection. Will be sent, in order, on reconnect. */
  | 'pending'
  /** The server rejected it. It needs the owner to look at it — it will not retry itself. */
  | 'failed';

export interface QueuedWriteError {
  status?: number;
  code?: string;
  message: string;
  /** A 409 means someone else changed the record while this write was waiting. */
  conflict: boolean;
}

/** A queued write as it is stored. The sequence number is IndexedDB's out-of-line key. */
export interface QueuedWriteRecord {
  /** Stable id used for idempotency on the server and for `discard`. */
  id: string;
  createdAt: number;
  method: QueueableMethod;
  path: string;
  body?: unknown;
  /** i18n key naming the write, so the pending list can describe it in the shop's language. */
  labelKey: string;
  labelParams?: Record<string, string | number>;
  /** Query keys to invalidate once the write lands. */
  invalidate?: string[][];
  attempts: number;
  status: QueuedWriteStatus;
  lastError?: QueuedWriteError;
}

/** A stored write plus its sequence number — the submission order the queue replays in. */
export interface QueuedWrite extends QueuedWriteRecord {
  seq: number;
}

export interface OfflineDbSchema extends DBSchema {
  [WRITE_QUEUE_STORE]: {
    key: number;
    value: QueuedWriteRecord;
    indexes: { 'by-id': string };
  };
}

export type OfflineDb = IDBPDatabase<OfflineDbSchema>;

/**
 * The store uses out-of-line auto-incrementing keys on purpose: IndexedDB itself becomes the
 * source of truth for submission order, and that order is monotonic across page reloads
 * without us having to persist a counter of our own.
 */
export function openOfflineDb(name: string = OFFLINE_DB_NAME): Promise<OfflineDb> {
  return openDB<OfflineDbSchema>(name, OFFLINE_DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(WRITE_QUEUE_STORE)) {
        const store = db.createObjectStore(WRITE_QUEUE_STORE, { autoIncrement: true });
        store.createIndex('by-id', 'id', { unique: true });
      }
    },
  });
}

/** True when this browser can persist a queue at all. Private modes sometimes cannot. */
export function isIndexedDbAvailable(): boolean {
  try {
    return typeof indexedDB !== 'undefined' && indexedDB !== null;
  } catch {
    return false;
  }
}
